import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { muiTheme } from 'app/themes/materialUi';
import { Menu, MenuItem, ListItemIcon, ListItemText, Button, Typography, Box } from '@mic3/platform-ui';
import moment from 'moment';
import genUuidv4 from 'uuid/v4';

import Widget, { WidgetBase } from 'app/components/atoms/Widget/Widget';
import { canViewWidget, getAddWidgetMenu, getDateRange } from 'app/containers/LandingPage/dashboardUtils';
import history from 'store/History';

import Icon from 'app/components/atoms/Icon/Icon';
import { useDispatch, useSelector } from 'react-redux';
import Calendar from 'app/components/molecules/Calendar/Calendar';
import { loadCalendarTasks } from 'store/actions/abox/taskActions';
import { useLazyQuery } from '@apollo/client';
import { DASHBOARD_MAP_QUERY } from 'graphql/dashboard/dashboardEntitiesQuery';
import { getPermissions } from 'app/config/rolesConfig';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import { datefy, getDate } from 'app/utils/utils';
import { get } from 'app/utils/lo/lo';
import { getPriorityColor } from 'app/config/aboxConfig';
import theme from 'app/themes/theme.default';
import { openTaskFilter } from 'app/utils/filter/filterUtils';

const IFrame = styled.iframe.attrs((props) => ({
    height: props?.height || '100%',
    width: props?.width || '100%',
}))`
    width: ${({ width }) => width} !important;
    height: ${({ height }) => height} !important;
    border: none;
    display: block;
`;

const Flex = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${(props) => props}
`;

const PlusButtonStyled = styled(Typography)`
    font-family: Rubik;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 3.5%;
    text-align: center;
    margin-right: 12px;
`;
const AddNewButton = styled(Button)`
    margin-top: 10px;
    color: ${({ theme }) => theme.material.colors.text.landingActiveButton} !important;
    font-family: Rubik;
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 3.5%;
    text-align: center;
`;

const StyledCalendar = styled(Calendar)`
    .rbc-toolbar {
        padding: 0 1rem;
    }
`;

export const CreateNewWidget = ({ onAddWidget, toggleAddDashModal, selectedDashboardId }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const { data } = useSelector((state) => state.dashboard.dashboardEntities);
    const userProfile = useSelector((state) => state.user.profile);
    const dashboards = data?.records || [];
    const selectedDashboard = dashboards.find((d) => d?.id === selectedDashboardId);
    const permissions = getPermissions(selectedDashboard?.role);

    const handleClick = (event) => {
        if (!dashboards.length) {
            toggleAddDashModal(true);
        } else {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onItemClick = (item) => {
        onAddWidget(item);
        handleClose();
    };

    const getMessage = () => {
        let message = dashboards?.length ? 'Expand your dashboards with personalised widgets' : 'Create a new dashboard to get started.';
        if (!permissions.canEdit) {
            message = 'You do not have permission to edit this dashboard.';
        }
        return (
            <Typography variant='body1' align='center' style={{ color: muiTheme.colors.text.caption, padding: '12px' }}>
                {message}
            </Typography>
        );
    };

    return (
        <WidgetBase style={{ maxWidth: 451, maxHeight: 365, margin: '40px 20px' }}>
            <div className='drag-handle' style={{ height: '30px', width: '100%', cursor: 'pointer' }} />
            <Flex justifyContent='center' alignItems='center' height='335px'>
                <Icon type='mdi' name='plus' size='xxl' color='secondary' />
                {getMessage()}
                {permissions?.canEdit ? (
                    <AddNewButton aria-controls='widget-menu' aria-haspopup='true' variant='text' onClick={handleClick}>
                        <>
                            <PlusButtonStyled variant='h6' style={{ marginRight: '8px' }}>
                                +
                            </PlusButtonStyled>
                            ADD NEW
                        </>
                    </AddNewButton>
                ) : null}
            </Flex>
            <Menu id='widget-menu' anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                {getAddWidgetMenu(userProfile).map((item, index) => (
                    <MenuItem key={index} onClick={() => onItemClick(item)}>
                        <ListItemIcon>
                            <Icon name={item.icon} type={item.iconType} />
                        </ListItemIcon>
                        <ListItemText primary={item.name} />
                    </MenuItem>
                ))}
            </Menu>
        </WidgetBase>
    );
};

export const DashboardIFrame = ({ item, menuItemClick, permissions, type }) => {
    const { widgetName, url } = item || {};
    const [isLoading, setIsLoading] = useState(true);
    const iframeRef = useRef();

    useEffect(() => {
        setIsLoading(true);
    }, [url]);

    const handleIframeLoad = () => {
        setIsLoading(false);
    };

    const refreshIframe = useCallback(() => {
        if (iframeRef.current) {
            setIsLoading(true);
            iframeRef.current.src = url;
        }
    }, [url]);

    const handleMenuClick = (menu) => {
        if (menu === 'Refresh') {
            refreshIframe();
        } else {
            menuItemClick && menuItemClick(menu);
        }
    };

    return (
        <Widget
            loading={isLoading}
            content={<IFrame ref={iframeRef} onLoad={handleIframeLoad} title={widgetName} src={url} />}
            hideFooter={true}
            title={item.widgetName}
            menuItemClick={handleMenuClick}
            permissions={permissions}
            item={item}
            type={type}
        />
    );
};

export const DashboardCalendar = ({ item, menuItemClick, permissions, type }) => {
    const defaultView = item?.defaultView;
    const dispatch = useDispatch();
    const [view, setView] = useState(defaultView || 'day');
    const { isLoading, records } = useSelector((state) => state.abox.task.calendar);
    const { id } = useSelector((state) => state.user.profile || {});
    const calendarId = useMemo(() => genUuidv4(), []);

    const { start, end } = useMemo(() => getDateRange(defaultView), [defaultView]);
    const canView = canViewWidget(item, type);
    const loadEvents = useCallback(
        (start, end) => {
            if (!canView) return;
            dispatch(
                loadCalendarTasks(id, start, end, {
                    filterBy: [openTaskFilter],
                })
            );
        },
        [dispatch, id, canView]
    );

    useEffect(() => {
        loadEvents(start, end);
        setView(defaultView);
    }, [defaultView, start, end, loadEvents]);

    const buildEvents = useMemo(() => {
        return records
            .filter((task) => task)
            .map((task) => {
                const { primary, name, ...event } = task;
                const { dueDate, priority, closedDate } = primary || {};
                let end = datefy(dueDate);
                let start = getDate(primary, 'startDate');
                if (!start && end) {
                    start = moment(end)
                        .startOf('day')
                        .toDate();
                    end = moment(end)
                        .endOf('day')
                        .toDate();
                }
                if (start && !end) {
                    start = moment(start)
                        .startOf('day')
                        .toDate();
                    end = moment(start)
                        .endOf('day')
                        .toDate();
                }

                return { ...event, title: name || 'No Name', start, end, priority, closedDate };
            })
            .filter(({ start, end }) => start && end);
    }, [records]);

    const handleMenuClick = (menu) => {
        if (menu === 'Refresh') {
            dispatch(loadCalendarTasks(id, start, end));
        } else {
            menuItemClick && menuItemClick(menu);
        }
    };

    const goToTask = useCallback(({ id }) => {
        history.push(`/abox/task/${id}`);
    }, []);

    const buildEventProp = ({ priority, closedDate }, start, end, isSelected) => {
        const style = {};
        const priorityColor = closedDate ? 'disabled' : getPriorityColor(priority);
        const backgroundColor = String(get(theme, `priorityColors.${priorityColor}`));
        if (backgroundColor) {
            style.backgroundColor = `${backgroundColor}90`;
        }
        return { style };
    };

    return (
        <Widget
            hideFooter={true}
            title={item.widgetName}
            menuItemClick={handleMenuClick}
            permissions={permissions}
            item={item}
            type={type}
            content={
                <StyledCalendar
                    calendarId={`dashboard_calendar_${calendarId}`}
                    isLoading={isLoading}
                    events={buildEvents}
                    view={view}
                    onView={(v) => setView(v)}
                    onSelectEvent={goToTask}
                    eventPropGetter={buildEventProp}
                    onDateRangeChange={({ start, end }) => loadEvents(start, end)}
                />
            }
        />
    );
};

export const LinksWidget = ({ item, menuItemClick, type, permissions }) => {
    const formRef = useRef(null);

    const components = useMemo(() => {
        return [
            {
                type: 'groupRepeat',
                children: [
                    {
                        type: 'hyperlink',
                        properties: {
                            name: 'link',
                            label: '',
                        },
                        constraints: { required: true },
                    },
                ],
                properties: {
                    name: 'linksData',
                },
            },
        ];
    }, []);

    return (
        <Widget
            hideFooter={true}
            title={item.widgetName}
            menuItemClick={menuItemClick}
            permissions={permissions}
            type={type}
            item={item}
            content={<FormGenerator disabled ref={formRef} components={components} data={item} />}
        />
    );
};

export const MapWidgetPreview = ({ item, menuItemClick, permissions, type }) => {
    const { id } = item?.map || {};
    const canView = canViewWidget(item, type);
    const [fetchDashboardMap, { data, loading, error, refetch, networkStatus }] = useLazyQuery(DASHBOARD_MAP_QUERY, {
        notifyOnNetworkStatusChange: true,
    });
    const thumbnail = data?.entity?.primary?.['system_map/thumbnail'];
    const isRefetching = networkStatus === networkStatus.refetch;
    useEffect(() => {
        if (id && canView) {
            fetchDashboardMap({ variables: { id } });
        }
    }, [id, canView, fetchDashboardMap]);

    const handleMenuClick = (menu) => {
        if (menu === 'Refresh') {
            refetch();
        } else {
            menuItemClick && menuItemClick(menu);
        }
    };

    return (
        <Widget
            loading={loading || isRefetching}
            hideFooter
            title={item.widgetName}
            menuItemClick={handleMenuClick}
            permissions={permissions}
            item={item}
            type={type}
            content={
                <Flex justifyContent='center' alignItems='center' height='100%'>
                    {error && <h6>Something went wrong!</h6>}
                    {thumbnail ? (
                        <Box style={{ cursor: 'pointer' }} onClick={() => history.push(`/maps/${id}`)}>
                            <img
                                src={thumbnail}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                }}
                            />
                        </Box>
                    ) : (
                        <Icon name='map' size='xxl' onClick={() => history.push(`/maps/${id}`)} />
                    )}
                </Flex>
            }
        />
    );
};
